import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleInfo,
  faChevronDown,
  faChevronUp,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { exportDailyRankingToExcel } from "../utils/excelExport";
import * as XLSX from "xlsx";

const DailyRankingTable = ({ weekData }) => {
  const navigate = useNavigate();
  const [collapsedDays, setCollapsedDays] = useState({});
  const [downloadingDays, setDownloadingDays] = useState({});
  const [activeDayTabs, setActiveDayTabs] = useState({});
  const [firstDayUsers, setFirstDayUsers] = useState([]);

  // Load first day users from JSON
  useEffect(() => {
    const loadFirstDayData = async () => {
      try {
        const response = await fetch("/firstdayincurrentevent.json");
        if (response.ok) {
          const data = await response.json();
          if (data && data.listTopUser) {
            setFirstDayUsers(data.listTopUser.map((item) => item.user));
          }
        }
      } catch (error) {
        console.error("Error loading first day data:", error);
      }
    };
    loadFirstDayData();
  }, []);

  const handleUser = (user) => {
    navigate(`/userActivities/${user.id}/${user.lastName}/${user.firstName}`);
  };

  const toggleDay = (date) => {
    setCollapsedDays((prev) => ({
      ...prev,
      [date]: !prev[date],
    }));
  };

  const handleDownloadExcel = async (dayData, date) => {
    try {
      setDownloadingDays((prev) => ({ ...prev, [date]: true }));
      await exportDailyRankingToExcel(dayData);
    } catch (error) {
      console.error("Error downloading Excel:", error);
    } finally {
      setDownloadingDays((prev) => ({ ...prev, [date]: false }));
    }
  };

  // Get users who are in first day but haven't posted today
  const getUsersNotPostedToday = (day) => {
    const postedUserIds = new Set(day.listTopUser.map((item) => item.user.id));
    return firstDayUsers.filter((user) => !postedUserIds.has(user.id));
  };

  // Export users not posted today to Excel
  const handleDownloadNotPostedExcel = async (users, date) => {
    try {
      setDownloadingDays((prev) => ({ ...prev, [`notPosted_${date}`]: true }));

      const workbook = XLSX.utils.book_new();
      const excelData = users.map((user) => ({
        ID: user.id,
        "Mã KH": user.code,
        Họ: user.lastName || "",
        Tên: user.firstName || "",
        SĐT: user.phoneNumber || "",
        "Số Ngày Đăng": user.totalPostsInCurrentEvent || 0,
        "Doanh thu": user.totalSales
          ? Number(user.totalSales).toLocaleString("vi-VN")
          : "0",
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const dateFormatted = dayjs(date).format("DD-MM-YYYY");
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        `ChuaDang_${dateFormatted}`,
      );

      const fileName = `User_chua_dang_${dateFormatted}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error("Error downloading Excel:", error);
    } finally {
      setDownloadingDays((prev) => ({ ...prev, [`notPosted_${date}`]: false }));
    }
  };

  const setActiveDayTab = (date, tab) => {
    setActiveDayTabs((prev) => ({
      ...prev,
      [date]: tab,
    }));
  };

  const getActiveDayTab = (date) => {
    return activeDayTabs[date] || "top";
  };

  return (
    <div className="space-y-8">
      {weekData.map((day, index) => {
        const isCollapsed = !collapsedDays[day.date];
        const isDownloading = downloadingDays[day.date];
        return (
          <div key={day.date} className="relative">
            <div
              className={`sticky top-0 z-10 bg-white dark:bg-gray-800 rounded-xl shadow-lg transition-all duration-300 ${
                isCollapsed ? "py-2" : ""
              }`}
            >
              <div
                className={`px-6 ${
                  isCollapsed ? "py-2" : "py-4"
                } border-b border-gray-200 dark:border-gray-700 flex justify-between items-center`}
              >
                <div
                  className="flex items-center cursor-pointer flex-1"
                  onClick={() => toggleDay(day.date)}
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Ngày {dayjs(day.date).format("DD/MM/YYYY")}
                    <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                      ({day.listTopUser.length} người trong top)
                    </span>
                  </h3>
                  <FontAwesomeIcon
                    icon={isCollapsed ? faChevronDown : faChevronUp}
                    className="text-gray-500 dark:text-gray-400 ml-2"
                  />
                </div>

                {/* Nút Download Excel */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadExcel(day, day.date);
                  }}
                  disabled={isDownloading}
                  className={`ml-4 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isDownloading
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-green-600 hover:bg-green-700 text-white hover:shadow-lg"
                  }`}
                >
                  {isDownloading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Đang tải...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faDownload} className="mr-2" />
                      Tải Excel
                    </div>
                  )}
                </button>
              </div>
            </div>

            <AnimatePresence>
              {!isCollapsed && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  {/* Day Tabs */}
                  <div className="px-6 pt-4 pb-2 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setActiveDayTab(day.date, "top")}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          getActiveDayTab(day.date) === "top"
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                        }`}
                      >
                        Top Users ({day.listTopUser.length})
                      </button>
                      <button
                        onClick={() => setActiveDayTab(day.date, "notPosted")}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          getActiveDayTab(day.date) === "notPosted"
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                        }`}
                      >
                        Chưa đăng bài ({getUsersNotPostedToday(day).length})
                      </button>
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="overflow-x-auto">
                    {getActiveDayTab(day.date) === "top" ? (
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              ID
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Mã KH
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Họ và Tên
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              SĐT
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Số Ngày Đăng
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Giờ post đầu
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Doanh thu
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Chi tiết
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {day.listTopUser.length === 0 ? (
                            <tr>
                              <td
                                colSpan="8"
                                className="px-6 py-4 text-center text-gray-500 dark:text-gray-400"
                              >
                                Không có dữ liệu
                              </td>
                            </tr>
                          ) : (
                            day.listTopUser.map((userTop) => {
                              const user = userTop.user;
                              const rank = userTop.rankInfo.rankDateToday;
                              return (
                                <tr
                                  key={user.id}
                                  className={`transition-colors duration-150 relative hover:bg-gray-100 dark:hover:bg-gray-600`}
                                >
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white text-center relative">
                                    {user.id}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-center">
                                    {user.code}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-center">
                                    {user.firstName} {user.lastName}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                                    {user.phoneNumber}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                                    {user.totalPostsInCurrentEvent}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                                    {dayjs(
                                      rank.timeFirstValidPostInToday,
                                    ).format("HH:mm:ss.SSS DD/MM")}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                                    {user.totalSales
                                      ? Number(user.totalSales).toLocaleString(
                                          "vi-VN",
                                        )
                                      : "0"}
                                  </td>
                                  <td
                                    className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center cursor-pointer"
                                    onClick={() => handleUser(user)}
                                  >
                                    <FontAwesomeIcon icon={faCircleInfo} />
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    ) : (
                      <div>
                        <div className="px-6 py-3 flex justify-end">
                          <button
                            onClick={() =>
                              handleDownloadNotPostedExcel(
                                getUsersNotPostedToday(day),
                                day.date,
                              )
                            }
                            disabled={downloadingDays[`notPosted_${day.date}`]}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                              downloadingDays[`notPosted_${day.date}`]
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-green-600 hover:bg-green-700 text-white hover:shadow-lg"
                            }`}
                          >
                            {downloadingDays[`notPosted_${day.date}`] ? (
                              <div className="flex items-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Đang tải...
                              </div>
                            ) : (
                              <div className="flex items-center">
                                <FontAwesomeIcon
                                  icon={faDownload}
                                  className="mr-2"
                                />
                                Tải Excel
                              </div>
                            )}
                          </button>
                        </div>
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                ID
                              </th>
                              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Mã KH
                              </th>
                              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Họ và Tên
                              </th>
                              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                SĐT
                              </th>
                              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Số Ngày Đăng
                              </th>
                              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Doanh thu
                              </th>
                              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Chi tiết
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {getUsersNotPostedToday(day).length === 0 ? (
                              <tr>
                                <td
                                  colSpan="7"
                                  className="px-6 py-4 text-center text-gray-500 dark:text-gray-400"
                                >
                                  Không có user nào chưa đăng bài
                                </td>
                              </tr>
                            ) : (
                              getUsersNotPostedToday(day).map((user) => {
                                return (
                                  <tr
                                    key={user.id}
                                    className={`transition-colors duration-150 relative hover:bg-gray-100 dark:hover:bg-gray-600`}
                                  >
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white text-center relative">
                                      {user.id}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-center">
                                      {user.code}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-center">
                                      {user.firstName} {user.lastName}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                                      {user.phoneNumber}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                                      {user.totalPostsInCurrentEvent || 0}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                                      {user.totalSales
                                        ? Number(
                                            user.totalSales,
                                          ).toLocaleString("vi-VN")
                                        : "0"}
                                    </td>
                                    <td
                                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center cursor-pointer"
                                      onClick={() => handleUser(user)}
                                    >
                                      <FontAwesomeIcon icon={faCircleInfo} />
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

export default DailyRankingTable;
